import { useState, useEffect } from 'react';
import { useCompanyProfile } from '../../hooks/useCompanyProfile';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { validatePdfFile } from '../../lib/uploadValidation';

export default function NDASetup() {
  const { user } = useAuth();
  const { profile, loading, saving, save } = useCompanyProfile();
  const [ndaUrl, setNdaUrl] = useState('');
  const [ndaLink, setNdaLink] = useState('');
  const [countersignRequired, setCountersignRequired] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (profile) {
      setNdaUrl(profile.nda_document_url || '');
      setNdaLink(profile.nda_link || '');
      setCountersignRequired(profile.countersign_required !== false);
    }
  }, [profile]);

  const uploadNDA = async (file) => {
    const err = validatePdfFile(file);
    if (err) { alert(err); return; }
    setUploading(true);
    const path = `company-ndas/${user.id}/${Date.now()}-${file.name}`;
    const { error } = await supabase.storage.from('event-assets').upload(path, file);
    if (error) { alert('Upload failed: ' + error.message); setUploading(false); return; }
    const { data: urlData } = supabase.storage.from('event-assets').getPublicUrl(path);
    setNdaUrl(urlData.publicUrl);
    setNdaLink('');
    setSaved(false);
    setUploading(false);
  };

  const handleSave = async () => {
    try {
      const currentVersion = profile?.nda_version || 1;
      const ndaChanged = ndaUrl !== (profile?.nda_document_url || '') || ndaLink !== (profile?.nda_link || '');

      await save({
        nda_document_url: ndaUrl || null,
        nda_link: ndaLink || null,
        countersign_required: countersignRequired,
        nda_version: ndaChanged ? currentVersion + 1 : currentVersion,
      });
      setSaved(true);
    } catch (err) {
      alert('Save failed: ' + err.message);
    }
  };

  if (loading) return <div className="text-sm text-gray-400">Loading NDA settings...</div>;

  const inputCls = "w-full px-4 py-3 rounded-input border-2 border-vault-border text-sm font-body text-vault-navy bg-white/70 focus:border-vault-gold focus:outline-none transition-colors";
  const labelCls = "block text-[12px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5";
  const hasNda = ndaUrl || ndaLink;

  return (
    <div className="glass p-7 max-w-2xl">
      <h2 className="font-heading text-xl text-vault-navy mb-1">NDA Management</h2>
      <p className="text-sm text-gray-400 mb-5">
        Inventors must sign your NDA before they can submit ideas to you.
        {profile?.nda_version > 1 && <span className="ml-2 text-amber-600 font-medium">Version {profile.nda_version}</span>}
      </p>

      <div className="space-y-5">
        {/* Upload PDF */}
        <div>
          <label className={labelCls}>Upload NDA (PDF)</label>
          {ndaUrl && !ndaLink && (
            <div className="flex items-center gap-2 mb-2">
              <a href={ndaUrl} target="_blank" rel="noreferrer" className="text-vault-gold text-sm font-semibold hover:underline truncate">
                Current NDA document
              </a>
              <button onClick={() => { setNdaUrl(''); setSaved(false); }} className="text-vault-error text-xs">Remove</button>
            </div>
          )}
          <input type="file" accept=".pdf" onChange={e => e.target.files[0] && uploadNDA(e.target.files[0])} disabled={uploading} />
        </div>

        <div className="flex items-center gap-3">
          <div className="flex-1 h-px bg-vault-border" />
          <span className="text-xs text-gray-400 uppercase">or</span>
          <div className="flex-1 h-px bg-vault-border" />
        </div>

        {/* External link */}
        <div>
          <label className={labelCls}>External NDA Link (DocuSign, HelloSign, etc.)</label>
          <input
            className={inputCls}
            value={ndaLink}
            onChange={e => { setNdaLink(e.target.value); if (e.target.value) setNdaUrl(''); setSaved(false); }}
            placeholder="https://app.hellosign.com/..."
          />
        </div>

        {/* Countersign toggle */}
        <div className="border-t border-vault-border pt-5">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={countersignRequired}
              onChange={e => { setCountersignRequired(e.target.checked); setSaved(false); }}
              className="w-5 h-5 accent-amber-500"
            />
            <div>
              <div className="text-sm font-semibold text-vault-navy">Require countersignature</div>
              <div className="text-xs text-gray-400 mt-0.5">
                {countersignRequired
                  ? 'You will countersign after the inventor signs (bilateral execution)'
                  : 'You will acknowledge receipt of the signed NDA (unilateral)'}
              </div>
            </div>
          </label>
        </div>

        {!hasNda && (
          <div className="bg-amber-50 border border-amber-200 rounded-btn p-4">
            <p className="text-sm text-amber-700">Upload an NDA or provide an external link to enable the NDA signing flow for your inventors.</p>
          </div>
        )}

        <div className="flex items-center gap-4">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2.5 rounded-btn text-sm font-semibold text-white shadow-btn disabled:opacity-50 transition-all"
            style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}
          >
            {saving ? 'Saving...' : 'Save NDA Settings'}
          </button>
          {saved && <span className="text-emerald-600 text-sm font-medium">Saved!</span>}
        </div>
      </div>
    </div>
  );
}